const filterNodes = {
  quickShopModal: document.querySelector('quick-shop'),
  parent: document.getElementById('collectionFilters'),
  gridsContainer: document.getElementById('products-listing'),
  collectionBanner: document.querySelector('[data-collectionBanner]'),
  subCollectionLinks: Array.from(document.getElementsByClassName('subcollection-links')),
  activeFilters: document.querySelector('[data-activeFilters]'),
  drawerFooter: document.querySelector('[data-filterFooter]'),
  openDrawerBtns: document.querySelectorAll('[data-openFilterDrawer]'),
  paginationParent: document.querySelector('[data-paginationParent]'),
  closeDrawer: document.querySelector('[data-closeDrawer]')
}

class CollectionFilters {
  /**
   * Self-contained debounce. Mirrors Utility.debounce, but lives here so the
   * filter form is wired up even if utility.js has not executed yet.
   *
   * @param {function} fn - function to execute
   * @param {integer} wait - timeout in ms
   */
  static debounce(fn, wait) {
    let t;
    const debounced = function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced;
  }

  constructor() {
    this.infiniteScrollObserver = null;
    this.infiniteScrollLoading = false;
    this.filterForm = filterNodes?.parent?.querySelector('form');

    filterNodes?.parent?.addEventListener('keyup', (event) => {
      if(event.code && event.code.toUpperCase() === 'ESCAPE') filterNodes.closeDrawer.dispatchEvent(new Event('click'));
    });

    // Do NOT reach for `Utility` here without a fallback. utility.js and this file
    // are separate <script>s, and utility.js used to be `async` while this one is
    // `defer`, so a slow connection could land this one first. When that happened
    // the old `try { Utility.debounce(...) }` threw, the catch swallowed it, and the
    // form listeners below were never attached at all -- filters looked tappable but
    // never applied, with nothing in the console. That is the most likely cause of
    // the "filters do nothing on iPhone" report: script order losing a race, which
    // phones lose more often than desktops. See also snippets/script-tags.liquid.
    this.debouncedOnSubmit = CollectionFilters.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    if (this.filterForm) {
      const onFilterChange = (evt) => {
        if (!window.globalVariables?.templateSuffix?.includes('saddle-selector')) {
          // Range inputs are applied by the drawer's "View results" button (mobile)
          // or PriceRangeSlider's change handler (desktop), not on every drag tick.
          if (evt && evt.target.type == 'range') return false;
        }
        this.debouncedOnSubmit(evt);
      };

      this.filterForm.addEventListener('input', onFilterChange);
      // Belt and braces: `change` is the older, more universally implemented event
      // for checkbox/radio toggles. Listening to both costs nothing, because the
      // 500ms debounce collapses the input/change pair into a single request.
      this.filterForm.addEventListener('change', onFilterChange);
      this.filterForm.addEventListener('submit', this.debouncedOnSubmit.bind(this));

      // window.addEventListener('popstate', this.onHistoryChange.bind(this));
    }
    
    this.bindEvents();
    this.dynamicEleEvents();
    this._initScrollRestoration();
    this._setupScrollUpLoadPrevious();
    this._setupScrollDownLoadMore();
  }

  /**
   * Auto-click the hidden "Load previous products" button — but ONLY while the
   * shopper is scrolling UP and the sentinel is within 1000px of the viewport
   * top. Scrolling down does nothing.
   */
  _setupScrollUpLoadPrevious() {
    this._lastScrollY = window.scrollY;
    this._loadPreviousOffset = 2000;

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      const scrollingUp = currentY < this._lastScrollY;
      this._lastScrollY = currentY < 0 ? 0 : currentY;

      if (!scrollingUp) return;

      const loadPrevious = document.getElementById('load-previous');
      if (!loadPrevious || loadPrevious.disabled || loadPrevious.classList.contains('loading')) return;

      const anchor = document.querySelector('[data-load-previous-sentinel]') || loadPrevious;
      const rect = anchor.getBoundingClientRect();
      const offset = this._loadPreviousOffset;
      const withinOffset = rect.top <= offset && rect.bottom > -offset;

      if (withinOffset) {
        loadPrevious.click();
      }
    }, { passive: true });
  }

  /**
   * Auto-load the next page while the shopper is scrolling DOWN — only when
   * infinite scroll is active. Load-more mode relies on the visible button click.
   */
  _setupScrollDownLoadMore() {
    this._lastScrollYDown = window.scrollY;
    this._loadMoreOffset = 500;

    window.addEventListener('scroll', () => {
      const paginationParent = document.querySelector('[data-paginationParent]');
      if (paginationParent?.dataset.infiniteScroll !== 'true') return;

      const currentY = window.scrollY;
      const scrollingDown = currentY > this._lastScrollYDown;
      this._lastScrollYDown = currentY < 0 ? 0 : currentY;

      if (!scrollingDown || this.infiniteScrollLoading) return;

      const loadMore = document.getElementById('load-more');
      if (!loadMore || !loadMore.href) return;

      const anchor = document.querySelector('[data-infinite-scroll-sentinel]') || loadMore;
      const rect = anchor.getBoundingClientRect();
      const offset = this._loadMoreOffset;
      const withinOffset = rect.top - window.innerHeight <= offset && rect.bottom > -offset;

      if (withinOffset) {
        this._loadMoreViaInfiniteScroll();
      }
    }, { passive: true });
  }

  /** Whether infinite scroll (not manual load-more) is the active pagination mode. */
  _isInfiniteScrollActive() {
    return document.querySelector('[data-paginationParent]')?.dataset.infiniteScroll === 'true';
  }

  /**
   * Keep load-more button visibility in sync with pagination mode after AJAX
   * updates — infinite hides the button; load-more keeps it visible.
   */
  _syncPaginationUI() {
    const parent = document.querySelector('[data-paginationParent]');
    const loadMore = document.getElementById('load-more');
    if (!parent || !loadMore) return;

    if (parent.dataset.infiniteScroll === 'true') {
      loadMore.classList.add('d-none');
      loadMore.setAttribute('tabindex', '-1');
      loadMore.setAttribute('aria-hidden', 'true');
    } else if (parent.dataset.type === 'loadmore') {
      loadMore.classList.remove('d-none');
      loadMore.removeAttribute('tabindex');
      loadMore.removeAttribute('aria-hidden');
    }
  }

  /**
   * bind dclick and keyup event to Cart Icons
   * bind keyUp event to Cart drawer component
   * bind Other inside element events
   *
  */
  bindEvents() {
    /** There are two open drawer buttons, one for Open filter drawer other for open sortby drawer **/
    if(filterNodes.openDrawerBtns.length > 0){
      filterNodes.openDrawerBtns.forEach(button => button.addEventListener('click', this.toggleFilterDrawer.bind(this)));
    }
    
    filterNodes.closeDrawer?.addEventListener('click', this.toggleFilterDrawer.bind(this));

    /**
     * "Clear all" inside the filter panel — the sidebar link and the mobile
     * drawer's button. Delegated on purpose: renderProductGrid swaps the drawer
     * footer's innerHTML on every update, and the sidebar link is a persistent
     * node that dynamicEleEvents() revisits, so direct binding would either be
     * lost or stack duplicates. One listener on the container survives both.
     *
     * The "Filtered by" chips (.filter-option-clear) are handled separately by
     * onActiveFilterClick, and call stopImmediatePropagation, so any chip that
     * also carries data-reset-filters never reaches this handler twice.
     */
    filterNodes.parent?.addEventListener('click', (event) => {
      const clearLink = event.target?.closest?.('[data-reset-filters], [data-clear-filters-mobile]');
      if (clearLink) this.onClearFiltersClick(event, clearLink);
    });

    this._updateSortByEvent();

    let paginateByValues = document.querySelectorAll('[data-custom-pagination] [name^="paginateBy"]');
    paginateByValues.forEach(input => input.addEventListener('change', this.paginateProductsBy.bind(this)));

    /** Only for horizontal filters - Close filters when clicked outside container **/
    const filterType = filterNodes.parent.dataset.filtertype;
    if(filterType == 'horizontal-filters'){
      document.body.addEventListener('click', (_event)=>{
        const openFilters = document.querySelectorAll('.filter__container.open');
        openFilters.forEach((target) => {
          target.querySelector('.tab_toggle').click();
        })
      });
  
      const filterContainers = document.querySelectorAll('.filter__container');
      filterContainers.forEach((target) => {
        target.addEventListener('click', (event)=>{
          event.stopPropagation();
        });
      });
    }
  }

  _updateSortByEvent(){
    let sortby_desktop = document.querySelectorAll('[data-sortByDesktop] .sortby_options');
    sortby_desktop.forEach(input => input.addEventListener('change', this.updateSortBy.bind(this)));
  }
  dynamicEleEvents() {
    /** Toggle each filter block container containing values for dropdown effect **/
    filterNodes.subCollectionLinks = Array.from(document.getElementsByClassName('subcollection-links'));
    filterNodes.subCollectionLinks.forEach(link => link.addEventListener('click', this._manageSubCollections.bind(this)));

    if(filterNodes.quickShopModal && typeof filterNodes.quickShopModal.updateEvents == 'function'){ 
      filterNodes.quickShopModal.updateEvents(); 
    }

    // "View results" in the mobile drawer. This is the guaranteed commit path: even
    // if the live auto-apply on `input`/`change` misses (or is still debouncing), a
    // tap here applies the current selection and closes the drawer.
    const applyBtn = filterNodes.parent.querySelector('[data-applyFilters]');
    if(applyBtn){
      applyBtn.addEventListener('click', () => {
        // Supersede any auto-apply still waiting out its debounce, so we don't fire
        // the same request twice and push two history entries.
        this.debouncedOnSubmit?.cancel?.();

        const searchParams = this._createQueryString(this.filterForm);

        // Close first so the drawer dismisses on the tap rather than after the fetch.
        this.closeFilterDrawer();
        this._collapseSortAndFilterPanel();

        // Nothing changed since the last applied state — just closing is the whole
        // job. Re-rendering would add a redundant history entry and break Back.
        if (!this._paramsMatchCurrentUrl(searchParams)) this.renderPage(searchParams);
      });
    }

    const paginationLinks = document.querySelectorAll('[data-pagelinks]');
    paginationLinks.forEach(link => link.addEventListener('click', this._managePagination.bind(this)));

    if (window.globalVariables?.templateSuffix?.includes('saddle-selector')) {
      this._updateSortByEvent();
    }
    this.colorOptionsStyling();
    this.bindActiveFilterButtonEvents();
    requestAnimationFrame(() => {
      this.setupInfiniteScroll();
      this._syncPaginationUI();
    });
  }

  _usesAppendPagination(paginationType) {
    return paginationType === 'loadmore' || paginationType === 'infinite';
  }

  _disconnectInfiniteScroll() {
    if (this.infiniteScrollObserver) {
      this.infiniteScrollObserver.disconnect();
      this.infiniteScrollObserver = null;
    }
  }

  setupInfiniteScroll() {
    this._disconnectInfiniteScroll();

    if (window.globalVariables?.templateSuffix?.includes('saddle-selector')) {
      return;
    }

    filterNodes.paginationParent = document.querySelector('[data-paginationParent]');
    if (filterNodes.paginationParent?.dataset.infiniteScroll !== 'true') {
      return;
    }

    const loadMore = document.getElementById('load-more');
    if (!loadMore) {
      return;
    }

    const sentinel = document.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel) {
      return;
    }

    this.infiniteScrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._loadMoreViaInfiniteScroll();
        }
      });
    }, { rootMargin: '500px 0px' });

    this.infiniteScrollObserver.observe(sentinel);
    this._checkInfiniteScrollLoad();
  }

  _checkInfiniteScrollLoad() {
    const loadMore = document.getElementById('load-more');
    const sentinel = document.querySelector('[data-infinite-scroll-sentinel]');
    if (!loadMore || !sentinel || this.infiniteScrollLoading) {
      return;
    }

    const sentinelRect = sentinel.getBoundingClientRect();
    const viewportBottom = window.innerHeight + 500;
    if (sentinelRect.top <= viewportBottom) {
      this._loadMoreViaInfiniteScroll();
    }
  }

  _loadMoreViaInfiniteScroll() {
    const loadMore = document.getElementById('load-more');
    if (this.infiniteScrollLoading || !loadMore?.href) {
      return;
    }

    this.infiniteScrollLoading = true;
    document.querySelector('[data-infinite-scroll-loader]')?.classList.remove('d-none');
    this.addNoIndexMetaTag();
    const loadedUrl = loadMore.href;
    this._appendingPage = this._getPageFromUrl(loadedUrl);
    this.renderGridFromFetch(loadedUrl, 'pagination').then(() => this._afterPageLoaded(loadedUrl));
  }

  /**
   * Keep scroll handling minimal: take manual control so a return to a deep
   * page (?page=N) doesn't try to restore a scroll position the shorter,
   * freshly-rendered page can't satisfy. The "Load previous products" button
   * (built when the page loads on ?page=N) lets the shopper pull the earlier
   * pages back in on demand.
   */
  _initScrollRestoration() {
    if (window.globalVariables?.templateSuffix?.includes('saddle-selector')) return;
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Each product cell is tagged with the page it came from. Opening a product
    // stamps THAT product's page into the URL (not just the deepest page loaded)
    // and saves its handle, so Back returns to the right page and scrolls to it.
    // The URL is left clean while browsing — it's only stamped on a product click.
    this._deepestPage = this._getPageFromUrl(window.location.href);
    this._tagGridChildren(this._deepestPage);

    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('a[href]');
      const href = link && link.getAttribute('href');
      if (href && /\/products\//.test(href)) {
        const pageEl = link.closest('[data-page]');
        const productPage = pageEl ? (parseInt(pageEl.dataset.page, 10) || 1) : (this._deepestPage || 1);
        this._stampPageInUrl(productPage);
        this._saveClickedProduct(href);
      }
    });

    // Safari/Firefox restore from the back/forward cache (bfcache) WITHOUT
    // re-running this script, so _restoreOnLoad never fires. `pageshow` with
    // event.persisted is the only signal we get — and the full DOM (all loaded
    // products) is already intact, so we just scroll back to the product.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this._restorePosition();
      }
    });

    this._restoreOnLoad();
  }

  /** Tag every product cell currently in the grid with the given page number. */
  _tagGridChildren(page) {
    const grid = document.getElementById('products-listing')?.querySelector('#product-grids');
    if (!grid) return;
    Array.from(grid.children).forEach((child) => { child.dataset.page = page; });
  }

  /** Tag the cells added to the grid from `startIndex` onward with the page. */
  _tagNewChildren(grid, startIndex, page) {
    for (let i = startIndex; i < grid.children.length; i += 1) {
      grid.children[i].dataset.page = page;
    }
  }

  /** sessionStorage key for the last product opened from this grid. */
  _productKey() {
    return `terry:lastProduct:${window.location.pathname}`;
  }

  /** Remember the product handle the shopper just opened, to scroll back to it. */
  _saveClickedProduct(href) {
    try {
      const handle = (href.split('/products/')[1] || '').split(/[?#]/)[0].split('/')[0];
      if (handle) Utility.setSessionStorage(this._productKey(), handle);
    } catch (err) {
      /* sessionStorage unavailable — fail silently */
    }
  }

  /** Read and clear the saved product handle (so it only scrolls once). */
  _consumeSavedProduct() {
    try {
      const handle = Utility.getSessionStorage(this._productKey());
      if (handle) Utility.removeFromSessionStorage(this._productKey());
      return handle || '';
    } catch (err) {
      return '';
    }
  }

  /** Read the `page` query parameter from a URL, defaulting to 1. */
  _getPageFromUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parseInt(parsed.searchParams.get('page'), 10) || 1;
    } catch (err) {
      return 1;
    }
  }

  /** Whether the active pagination appends content (load more / infinite). */
  _isAppendPaginationActive() {
    if (window.globalVariables?.templateSuffix?.includes('saddle-selector')) return false;
    const parent = document.querySelector('[data-paginationParent]');
    return !!parent && this._usesAppendPagination(parent.dataset.type);
  }

  /**
   * Stamp the given page into the URL via replaceState. Called right before the
   * shopper leaves for a product (with that product's own page), so Back returns
   * to the page that contains it — without churning the URL on Load more clicks.
   */
  _stampPageInUrl(page) {
    try {
      const p = page || 1;
      const current = new URL(window.location.href);
      if (p > 1) {
        current.searchParams.set('page', p);
      } else {
        current.searchParams.delete('page');
      }
      history.replaceState(history.state, '', `${current.pathname}${current.search}${current.hash}`);
    } catch (err) {
      /* no-op */
    }
  }

  /** Drop the `page` query parameter from the address bar (keeps it clean). */
  _removePageFromUrl() {
    try {
      const current = new URL(window.location.href);
      if (!current.searchParams.has('page')) return;
      current.searchParams.delete('page');
      history.replaceState(history.state, '', `${current.pathname}${current.search}${current.hash}`);
    } catch (err) {
      /* no-op */
    }
  }

  /**
   * Run after a Load more / infinite page is appended. We only TRACK the deepest
   * page here — the URL is left clean while browsing and is stamped with ?page
   * only when the shopper opens a product (see _stampPageInUrl).
   */
  _afterPageLoaded(loadedUrl) {
    this._deepestPage = Math.max(this._deepestPage || 1, this._getPageFromUrl(loadedUrl));
  }

  /**
   * Fetch a single page's product grid markup (used to rebuild earlier pages).
   * @param {Number} page
   * @param {String} sectionID
   */
  _fetchPageGrid(page, sectionID) {
    const target = new URL(window.location.href);
    if (page > 1) {
      target.searchParams.set('page', page);
    } else {
      target.searchParams.delete('page');
    }
    let fetchUrl = `${target.pathname}${target.search}`;
    const joiner = target.search ? '&' : '?';
    if (window.globalVariables.template == 'search') {
      const q = document.querySelector('[name="q"]')?.value || '';
      fetchUrl += `${joiner}sections=${sectionID}&q=${encodeURIComponent(q)}`;
    } else {
      fetchUrl += `${joiner}sections=${sectionID}`;
    }
    return fetch(fetchUrl)
      .then((response) => response.json())
      .then((response) => {
        const parsed = new DOMParser().parseFromString(response[sectionID] || '', 'text/html');
        return parsed.querySelector('#product-grids')?.innerHTML || '';
      })
      .catch(() => '');
  }

  /**
   * Whenever the page loads on a deep page (URL carries ?page=N, N>1) with
   * append pagination — i.e. a Back return, a reload, or a shared/Google deep
   * link — the server only renders page N, so the earlier pages are missing.
   * Add a "Load previous products" button above the grid so they can be pulled
   * back in on demand. (On a bfcache Back the full grid is already restored and
   * this script doesn't re-run, so no button is added — and none is needed.)
   */
  _restoreOnLoad() {
    const targetPage = this._getPageFromUrl(window.location.href);
    if (targetPage > 1 && this._isAppendPaginationActive()) {
      this._insertLoadPreviousButton(targetPage);
    }
    // Bring the shopper back to the exact product they opened (or, lacking that,
    // to the start of the deep page they were on).
    this._restorePosition();
  }

  /** Height of the sticky header, so the scroll target isn't hidden under it. */
  _headerOffset() {
    try {
      const value = getComputedStyle(document.documentElement).getPropertyValue('--bs-navdrawer');
      return (parseInt(value, 10) || 0) + 10;
    } catch (err) {
      return 0;
    }
  }

  /** Programmatic scroll — always instant (overrides theme `scroll-behavior: smooth`). */
  _scrollToY(top) {
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
  }

  /** Scroll so the given element sits just below the sticky header. */
  _scrollToElement(element) {
    if (!element) return;
    const scroll = () => {
      const top = element.getBoundingClientRect().top + window.scrollY - this._headerOffset();
      this._scrollToY(top);
    };
    requestAnimationFrame(() => requestAnimationFrame(scroll));
    // Re-adjust after images/layout settle — still instant, no animation.
    setTimeout(scroll, 500);
  }

  /**
   * On a deep-page load (?page=N), land the shopper at the bottom of the
   * rendered grid so they can scroll up into earlier pages.
   */
  _scrollToEndOfLoadedPage() {
    const productGrids = document.getElementById('products-listing')?.querySelector('#product-grids');
    const lastChild = productGrids?.lastElementChild;
    if (!lastChild) return;

    const scroll = () => {
      const rect = lastChild.getBoundingClientRect();
      const bottomPadding = 20;
      const scrollTarget = rect.bottom + window.scrollY - window.innerHeight + bottomPadding;
      this._scrollToY(scrollTarget);
    };
    requestAnimationFrame(() => requestAnimationFrame(scroll));
    setTimeout(scroll, 500);
  }

  /**
   * On a Back return, scroll to the product the shopper opened. If that product
   * isn't in the rendered grid (e.g. a fresh deep link with no saved click),
   * land at the bottom of the deep page so scrolling up can load earlier pages.
   */
  _restorePosition() {
    const productGrids = document.getElementById('products-listing')?.querySelector('#product-grids');
    if (!productGrids) return;

    const handle = this._consumeSavedProduct();
    const link = handle ? productGrids.querySelector(`a[href*="/products/${handle}"]`) : null;
    // Scroll to the whole card (the cell carries data-page), not just the link.
    let target = link ? (link.closest('[data-page]') || link) : null;
    if (!target && this._getPageFromUrl(window.location.href) > 1 && this._isAppendPaginationActive()) {
      this._scrollToEndOfLoadedPage();
      return;
    }
    this._scrollToElement(target);
  }

  /**
   * Insert the "Load previous products" button just above the product grid.
   * @param {Number} fromPage Page currently rendered (button loads fromPage-1 first).
   */
  _insertLoadPreviousButton(fromPage) {
    const productGrids = document.getElementById('products-listing')?.querySelector('#product-grids');
    if (!productGrids || document.getElementById('load-previous')) return;

    this._previousPageToLoad = fromPage - 1;
    if (this._previousPageToLoad < 1) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'row load-previous-parent g-0';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = '<div class="col-12 p-0 m-0">'
      + '<span data-load-previous-sentinel aria-hidden="true" style="display:block;height:1px;width:100%;visibility:hidden"></span>'
      + '<button type="button" id="load-previous" class="d-none" aria-hidden="true" tabindex="-1">'
      + 'Load previous products</button></div>';
    productGrids.parentNode.insertBefore(wrapper, productGrids);
    wrapper.querySelector('#load-previous').addEventListener('click', () => this._loadPreviousPage());
  }

  /** Fetch the next-earlier page and prepend it above the grid. */
  _loadPreviousPage() {
    const page = this._previousPageToLoad;
    const productsListing = document.getElementById('products-listing');
    const productGrids = productsListing?.querySelector('#product-grids');
    const sectionID = productsListing?.dataset.id;
    const button = document.getElementById('load-previous');
    if (!page || page < 1 || !productGrids || !sectionID || !button) return;

    button.setAttribute('disabled', 'disabled');
    button.classList.add('loading');

    this._fetchPageGrid(page, sectionID).then((html) => {
      if (html) {
        const scrollY = window.scrollY;
        const prevScrollHeight = document.documentElement.scrollHeight;
        // `afterbegin` keeps order: the earlier page lands above what's loaded.
        const beforeCount = productGrids.children.length;
        productGrids.insertAdjacentHTML('afterbegin', html);
        // Keep the viewport anchored — prepending above would otherwise jump scroll.
        const scrollDelta = document.documentElement.scrollHeight - prevScrollHeight;
        if (scrollDelta > 0) {
          this._scrollToY(scrollY + scrollDelta);
        }
        // Tag the just-prepended cells (they sit at the start) with their page.
        const added = productGrids.children.length - beforeCount;
        for (let i = 0; i < added; i += 1) {
          productGrids.children[i].dataset.page = page;
        }
        if (filterNodes.quickShopModal && typeof filterNodes.quickShopModal.updateEvents == 'function') {
          filterNodes.quickShopModal.updateEvents();
        }
      }
      this._previousPageToLoad = page - 1;
      if (this._previousPageToLoad < 1) {
        // Page 1 reached — every earlier product is now loaded, so drop the
        // button and clean ?page from the URL.
        document.querySelector('.load-previous-parent')?.remove();
        this._removePageFromUrl();
      } else {
        button.removeAttribute('disabled');
        button.classList.remove('loading');
      }
    });
  }

  /**
   * Collection filter form Input event
   * @param {event} event Event Instance
   */
  onSubmitHandler(event) {
    event.preventDefault();
    const searchParams = this._createQueryString(this.filterForm)
    this.renderPage(searchParams);
  }

  /**
   * click event To Remove current selections
   * @param {element} form Collection page filter form
   */
  _createQueryString(form){
    const formData = new FormData(form);
    if(form.querySelector('[name="filter.v.price.gte"]')){
      const priceGTE = form.querySelector('[name="filter.v.price.gte"]').min;
      const priceLTE = form.querySelector('[name="filter.v.price.lte"]').max;

      if(parseInt(formData.get('filter.v.price.gte')) == parseInt(priceGTE)){
        formData.delete('filter.v.price.gte');
      }
      if(parseInt(formData.get('filter.v.price.lte')) == parseInt(priceLTE)){
        formData.delete('filter.v.price.lte');
      }
    }

    // Drop empty values. An empty filter param filters nothing, but FormData still
    // reports blank number/text fields, which produced URLs like
    // `?q=&filter.v.price.gte=&filter.v.price.lte=` and made the "already applied"
    // comparison in _paramsMatchCurrentUrl miss against a clean URL.
    const entries = Array.from(formData).filter(([, value]) => String(value).trim() !== '');
    return new URLSearchParams(entries).toString();
  }

  /**
   * 
   * @param {String} searchParams Query Parameters
   * @param {String} updateURLHash true/false
  */
  renderPage(searchParams, updateURLHash = true) {
    filterNodes.gridsContainer.classList.add('loading');

    const url = `${window.location.pathname}?${searchParams}`;
    this.renderGridFromFetch(url, 'filter');
    try{
      if (window.globalVariables.template == 'search') {
        let q = Utility.getUrlParameter('q');
        let type = Utility.getUrlParameter('type');
        searchParams += `&q=${q}&type=${type}`
      }
    }catch(err){
      console.log(err,"<===Error")
    }
    if (updateURLHash) this.updateURLHash(searchParams);
  }

  /**
   * Add background color in label of Color options
   */
  colorOptionsStyling(){
    // Cosmetic only. Runs during construction, so it must never be able to throw —
    // it used to take the rest of dynamicEleEvents() down with it when utility.js
    // had not executed yet.
    if (typeof Utility === 'undefined' || typeof Utility.getSwatchStyle !== 'function') return;

    const colorSwatchContainer = filterNodes.parent.querySelector('[data-colorFilter]');
    if(colorSwatchContainer){
      const colorSwatches = colorSwatchContainer.querySelectorAll('.color-options');
      colorSwatches.forEach(swatch => {
        try {
          let colorHandle = swatch.querySelector('input[type="checkbox"]').dataset.handle;
          let swatchStyle = Utility.getSwatchStyle(colorHandle);
          swatch.querySelector('.option-label').setAttribute('style', swatchStyle);
        } catch (err) {
          /* one bad swatch must not stop the others */
        }
      });
    }
  }

  /**
   * click event To Remove current selections
   * @param {event} event Event Instance
   */
   onActiveFilterClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    let URLString = new URL(event.currentTarget.href).searchParams.toString();
    if(URLString != null) this.renderPage(URLString);
  }

  /**
   * Collapse the mobile "Sort & Filter" accordion that holds the Filters button,
   * so closing the drawer via "View results" leaves the page tidy rather than
   * dropping the shopper back onto an expanded panel.
   *
   * Clicking the panel's own toggle rather than stripping the class keeps
   * collapsiblePanel's aria-expanded and focus handling in step. The .open guard
   * doubles as a safety check: only collapsiblePanel adds that class, so if
   * custom-components.js has not loaded there is nothing to click and the bare
   * <a href="#"> can't jump the page.
   */
  _collapseSortAndFilterPanel() {
    document.querySelector('.short-mobile.open .panel_toggle')?.click();
  }

  /**
   * "Clear all" — wipe the selection and re-render over AJAX instead of
   * navigating, so the page never reloads. The mobile drawer stays open so the
   * shopper can pick new filters straight away.
   *
   * The link keeps a real href, so this still degrades to a normal page load if
   * the script has not initialised.
   *
   * @param {event} event Event Instance
   * @param {element} clearLink The clear link that was clicked
   */
  onClearFiltersClick(event, clearLink) {
    event.preventDefault();

    // A pending auto-apply would otherwise fire straight after and re-add the
    // filters we are about to clear.
    this.debouncedOnSubmit?.cancel?.();

    // Clear the inputs up front rather than waiting for the response: it is
    // instant feedback, and the price panel is never refreshed by the AJAX
    // render (price filter__containers carry no data-filter-handle), so its
    // fields would otherwise survive the clear and be re-applied by the next tap.
    this.resetFilterForm();

    // The link's href is this collection/search minus every filter, so its own
    // query string is exactly what should survive — sort_by, q, and nothing else.
    let searchParams = '';
    try {
      searchParams = new URL(clearLink.href, window.location.origin).searchParams.toString();
    } catch (err) {
      searchParams = '';
    }

    this.renderPage(searchParams);
  }

  /**
   * Return every filter input to its unfiltered state, matching what the server
   * renders for a collection with no filters applied.
   */
  resetFilterForm() {
    if (!this.filterForm) return;

    // Scope tightly to filter inputs. This same form also carries the sort_by
    // radios and the search `q` field, and the .sorting block is NOT refreshed by
    // the AJAX render — so clearing sort_by here would drop the shopper's sort
    // order from every later _createQueryString call, silently.
    //
    // The two selectors together cover all three filter shapes: list checkboxes
    // and price-list radios carry .filter-option, while the price range/number
    // inputs carry no class but are always named `filter.v.price.*`.
    const filterInputs = this.filterForm.querySelectorAll('input.filter-option, input[name^="filter."]');

    filterInputs.forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
        return;
      }

      if (input.type === 'range') {
        // gte back to the floor, lte to the ceiling — the state
        // _createQueryString strips out again.
        input.value = input.name && input.name.includes('.gte') ? input.min : input.max;
        // Non-bubbling, so PriceRangeSlider's own oninput redraws the printed
        // range without reaching the form's submit-on-input listener.
        input.dispatchEvent(new Event('input'));
        return;
      }

      input.value = '';
    });
  }

   /**
   * Update Window URL as per active filters
   * @param {event} event Event Instance
   */
  onHistoryChange(event) {
    console.log(event.state,'event.state')
    const searchParams = event.state?.searchParams || '';
    this.renderPage(searchParams, false);
  }

  /**
   * 
   * @param {String} url URL for fetching results
   * @param {String} type filter / pagination / sub_collection
   */
  renderGridFromFetch(url, type) {
    if(!url) return Promise.resolve();

    let sectionID = document.getElementById('products-listing').dataset.id;
    if (window.globalVariables.template == 'search') {
      url += `&sections=${sectionID}&q=${document.querySelector('[name="q"]').value}`
    }else{
      if(type == 'sub_collection'){
        url += `&sections=${sectionID},template-collection-banner`;
      }else{
        url += `&sections=${sectionID}`;
      }
    }
   
    return fetch(url)
      .then(response => response.json())
      .then((response) => {
        let gridHTML = response[sectionID];
        let bannerHTML = response['template-collection-banner'];
        this.renderProductGrid(type, gridHTML, bannerHTML);
      })
      .finally(() => {
        this.infiniteScrollLoading = false;
        document.querySelector('[data-infinite-scroll-loader]')?.classList.add('d-none');
      });
  }

  /**
   * 
   * @param {HTMlResponse} html 
   * @param {String} type filter / pagination / sub_collection
   */
  renderProductGrid(type, grid, banner) {
    const collectionParent = new DOMParser().parseFromString(grid, 'text/html');
    filterNodes.paginationParent = document.querySelector('[data-paginationParent]');
    let paginationType = filterNodes.paginationParent ? filterNodes.paginationParent.dataset.type : 'numbers';

    if(type == 'pagination' && this._usesAppendPagination(paginationType)){
      let productGrids = filterNodes.gridsContainer.querySelector('#product-grids');
      const beforeCount = productGrids.children.length;
      productGrids.insertAdjacentHTML('beforeend', collectionParent.querySelector('#product-grids').innerHTML);
      // Tag the just-appended cells with the page they belong to.
      this._tagNewChildren(productGrids, beforeCount, this._appendingPage || this._deepestPage || 1);

      let loadMoreBtn = document.getElementById('load-more');
      if(collectionParent.getElementById('load-more') && loadMoreBtn){   
        loadMoreBtn.parentNode.replaceChild(collectionParent.getElementById('load-more'), loadMoreBtn); 
      }else if(loadMoreBtn){
        loadMoreBtn.remove();
      }
      this._syncPaginationUI();
    }else{
      const gridsHTML = collectionParent.getElementById('products-listing')?.innerHTML;
      filterNodes.gridsContainer.innerHTML = gridsHTML;
      // Fresh listing (filter / sort / sub-collection) → server renders page 1.
      this._deepestPage = 1;
      this._tagGridChildren(1);
    }
    
    if (!window.globalVariables?.templateSuffix?.includes('saddle-selector')) {
      const filterPanels = collectionParent.querySelectorAll('#CollectionFiltersForm .filter__container');
      document.querySelectorAll('#CollectionFiltersForm .filter__container[data-filter-handle]:not([data-subcollections])').forEach(panel => panel.classList.add('d-none'));
      filterPanels.forEach((panel)=> {
        let filterBlock = document.querySelector('.filter__container[data-filter-handle="'+panel.dataset.filterHandle+'"]');
        if(filterBlock && filterBlock.querySelector('.filter__block')){
          filterBlock.querySelector('.filter__block').innerHTML = panel.querySelector('.filter__block').innerHTML;
          filterBlock.classList.remove('d-none');
        }
      });
      // const filterInnerHTML = collectionParent.getElementById('CollectionFiltersForm').innerHTML;
      // document.getElementById('CollectionFiltersForm').innerHTML = filterInnerHTML;
    }else{
      if (collectionParent.querySelectorAll('#CollectionFiltersForm .filter__container input:checked').length == 0) {        
        document.querySelectorAll('.filter__item input:checked')?.forEach(ele=>{
          ele.checked = false;
        })
      }
      const recommendedTestEle = collectionParent.querySelector('[data-recommended-text]')?.innerHTML;
      if (recommendedTestEle && document.querySelector('[data-recommended-text]')) {
        document.querySelector('[data-recommended-text]').innerHTML = recommendedTestEle;
      }
    }

    // Refresh the mobile drawer's action bar so its "View results (N)" count and
    // its "Clear all" link track the filters that were just applied.
    filterNodes.drawerFooter = filterNodes.drawerFooter || document.querySelector('[data-filterFooter]');
    const newDrawerFooter = collectionParent.querySelector('[data-filterFooter]');
    if (newDrawerFooter && filterNodes.drawerFooter) {
      filterNodes.drawerFooter.innerHTML = newDrawerFooter.innerHTML;
    }

    const clearBtn = collectionParent.querySelector('[data-reset-filters]');
    if (clearBtn == null || clearBtn.classList.contains('d-none')) {
      document.querySelector('[data-reset-filters]')?.classList.add('d-none');
    }else{
      document.querySelector('[data-reset-filters]')?.classList.remove('d-none');
    }
    const activeFiltersDefault = collectionParent.querySelector('[data-activeFilters="default"]');
    if(activeFiltersDefault) document.querySelector('[data-activeFilters="default"]').innerHTML = activeFiltersDefault.innerHTML;

    const activeFiltersTop = collectionParent.querySelector('[data-activeFilters="top"]');
    if(activeFiltersTop) document.querySelector('[data-activeFilters="top"]').innerHTML = activeFiltersTop.innerHTML;
    this.dynamicEleEvents();

    /******** Change banner on subcollection **********/
    if(type == 'sub_collection' && filterNodes.collectionBanner && banner){
      const bannerNode = new DOMParser().parseFromString(banner, 'text/html');
      let bannerHTML = bannerNode.querySelector('[data-collectionBanner]').innerHTML;
      filterNodes.collectionBanner.innerHTML = bannerHTML;
    }

    //let drawerOpen = filterNodes.parent.classList.contains('filter-active');
    //if(drawerOpen) filterNodes.closeDrawer.dispatchEvent(new Event('click'));


    let topBarFilterEle = document.querySelector('[data-top-bar-filter]');
    if(topBarFilterEle){
      topBarFilterEle.innerHTML = collectionParent.querySelector('[data-top-bar-filter]').innerHTML;
    }
    
    const skipScrollToFilter = type === 'pagination' && this._usesAppendPagination(paginationType);
    const filterAnchor = document.querySelector('[data-filter]');
    const anchorOffscreen = typeof Utility !== 'undefined' && typeof Utility.in_Viewport === 'function'
      ? !Utility.in_Viewport(filterAnchor)
      : false;
    // Don't scroll the page behind an open mobile drawer — the shopper can't see it,
    // and moving it under the drawer just loses their place when they close it.
    const drawerOpen = filterNodes.parent?.classList.contains('filter-active');
    if (!skipScrollToFilter && !drawerOpen && anchorOffscreen && filterAnchor) {
      window.scrollTo({
        top: document.querySelector('[data-filter]')?.offsetTop - 100,
        behavior: "smooth"
      });
    }
  }

  /**
   * Re-Binding events on active filters after ajax request
   */
  bindActiveFilterButtonEvents() {
    let removeActiveFilters = Array.from(document.getElementsByClassName('filter-option-clear'));
    removeActiveFilters.forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick.bind(this), { once: true });
    });
  }

  /**
   * Update the url
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * 
   * @param {event} event 
   */
  updateSortBy(event){
    let currentEle = event.currentTarget;
    let targetOpt = document.querySelector(`input[name="sort_by"][value="${currentEle.value}"]`); 
    if (window.globalVariables?.templateSuffix?.includes('saddle-selector')) {
      targetOpt = document.querySelector(`input[name="sort_by"]`); 
      targetOpt.value = document.querySelector('[name="custom_sort_by"]:checked')?.value;
    }
    if(targetOpt){
      targetOpt.checked = true;
      this.filterForm.dispatchEvent(new Event('submit', {
        bubbles: true,
        cancelable: true,
        once: true
      }));
    }
  }

  /**
   * 
   * @param {event} event 
   */
  paginateProductsBy(event){
    event.preventDefault();
    let requestData = { 
      attributes: { 'products_per_page': event.target.value } 
    };

    fetch("/cart/update", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    },
    body: JSON.stringify(requestData),
    }).then((data) =>{
      const searchParams = this._createQueryString(this.filterForm);
      const url = `${window.location.pathname}?${searchParams}`;
      this.renderGridFromFetch(url, 'pagination');
    });
  }

  /**
   * 
   * @param {event} event 
   */
  _manageSubCollections(event){
    if (!event.target.closest('[data-subCollections]')) {
      event.preventDefault();
      const _this =  event.currentTarget;
      const queryString = this._createQueryString(this.filterForm);
      let collURL = _this.href + '?' + queryString;
    
      this.renderGridFromFetch(collURL, 'sub_collection');
  
      setTimeout(() => {
        filterNodes.subCollectionLinks.forEach(link => link.classList.remove('active'));
        _this.classList.add('active');
        history.pushState({}, '', collURL);
      }, 500);
    }
  }

  /**
   * 
   * @param {event} event 
   */
  _managePagination(event){
    event.preventDefault();
    let _this = event.currentTarget;
    let paginationType = _this.closest('[data-paginationParent]')?.dataset.type;
    const appendMode = this._usesAppendPagination(paginationType);
    const infiniteScrollActive = _this.closest('[data-paginationParent]')?.dataset.infiniteScroll === 'true';
    if (infiniteScrollActive && this.infiniteScrollLoading) {
      return;
    }
    if (infiniteScrollActive) {
      this.infiniteScrollLoading = true;
      document.querySelector('[data-infinite-scroll-loader]')?.classList.remove('d-none');
    }
    const loadedUrl = _this.href;
    if (appendMode) this._appendingPage = this._getPageFromUrl(loadedUrl);
    const request = this.renderGridFromFetch(loadedUrl, 'pagination');

    // Add noindex meta tag when pagination is clicked
    this.addNoIndexMetaTag();

    if(!appendMode){
      setTimeout(() => {
        history.pushState({}, '', loadedUrl);
      }, 500);
    }else{
      // Track the deepest loaded page (the URL is only stamped with ?page when
      // the shopper opens a product — see _afterPageLoaded).
      request.then(() => this._afterPageLoaded(loadedUrl));
    }
  }

  /**
   * Add noindex meta tag to prevent search engine indexing of paginated pages
   */
  addNoIndexMetaTag(){
    console.log('Adding noindex meta tag...');
    // Check if noindex meta tag already exists
    let existingNoIndexTag = document.querySelector('meta[name="robots"][content="noindex"]');
    
    if (!existingNoIndexTag) {
      // Create and add the noindex meta tag
      let noIndexMeta = document.createElement('meta');
      noIndexMeta.name = 'robots';
      noIndexMeta.content = 'noindex';
      document.head.appendChild(noIndexMeta);
      console.log('Noindex meta tag added successfully');
    } else {
      console.log('Noindex meta tag already exists');
    }
  }

  /**
   * Close the mobile filter drawer, if it is open.
   */
  closeFilterDrawer(){
    if (!filterNodes.parent?.classList.contains('filter-active')) return;
    // Reuse the close button's handler so focus trap and overlay unwind the same way.
    filterNodes.closeDrawer?.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Whether a query string represents the state already in the address bar.
   * Compared order-insensitively, since the form's field order need not match the
   * order Shopify used in a filter URL the shopper arrived on.
   *
   * @param {String} searchParams Query parameters
   * @returns {Boolean}
   */
  _paramsMatchCurrentUrl(searchParams){
    const normalise = (value) => {
      const params = Array.from(new URLSearchParams(value || '').entries())
        .filter(([key]) => key !== 'page');
      params.sort();
      return JSON.stringify(params);
    };
    return normalise(searchParams) === normalise(window.location.search);
  }

  /**
   * Toggle Filter drawer
   * @param {event} event 
   */
  toggleFilterDrawer(event){
    event.preventDefault();
    let button = event.currentTarget;
    if(button.id == 'filter-drawer'){
      button.setAttribute('aria-expanded', true);
      button.setAttribute('tabindex', '-1');

      filterNodes.parent.classList.add('filter-active');

      if (typeof Utility !== 'undefined') {
        Utility.trapFocus?.(filterNodes.parent);
        Utility.forceFocus?.(filterNodes.closeDrawer);
      }
      if (typeof siteOverlay !== 'undefined') siteOverlay.prototype.showOverlay();
    }else{
      filterNodes.parent.classList.remove('filter-active');
      if(this.openFilterDrawer){
        this.openFilterDrawer.setAttribute('aria-expanded', false);
        this.openFilterDrawer.removeAttribute('tabindex');
      }
      if(this.openSortbyDrawer){
        this.openSortbyDrawer.setAttribute('aria-expanded', false);
        this.openSortbyDrawer.removeAttribute('tabindex');
      }

      if (typeof Utility !== 'undefined') Utility.removeTrapFocus?.(filterNodes.parent);
      if (typeof siteOverlay !== 'undefined') siteOverlay.prototype.hideOverlay();
    }
  }
}
typeof CollectionFilters !== 'undefined' && new CollectionFilters();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  /**
   * 
   * @param {event} event 
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();

    let _this = this;
    let form = this.closest('form');
    let sliderSections = this.getElementsByClassName("range-slider");

    for(const element of sliderSections){
      let sliders = element.getElementsByTagName("input");
      for(const element of sliders){
        if(element.type === "range" ){
          element.oninput = _this.getVals;
          element.oninput();

          if(form && form.id == 'CollectionFiltersForm'){
            element.onchange = (_evt)=>{
              if(window.innerWidth >= 992) form.dispatchEvent(new CustomEvent('submit'));
            } 
          }
        }
      }
    }
  }
  
  // Get Price value from both range slider
  getVals(){
    let parent = this.parentNode;
    let slides = parent.getElementsByTagName("input");
    let slide1 = parseFloat(slides[0].value);
    let slide2 = parseFloat(slides[1].value);
      
    // Neither slider will clip the other, so make sure we determine which is larger
    if( slide1 > slide2 ){ let tmp = slide2; slide2 = slide1; slide1 = tmp; }
    
    let displayElement = parent.getElementsByClassName("rangeValues")[0];
    if (!displayElement) return;
    if (typeof Utility === 'undefined' || typeof Utility.formatMoney !== 'function') {
      // utility.js not in yet — show plain numbers rather than throwing out of the
      // custom element constructor and leaving the slider dead.
      displayElement.innerHTML = `${slide1} - ${slide2}`;
      return;
    }
    displayElement.innerHTML = Utility.formatMoney((slide1*100), `${window.globalVariables.money_format} - ${Utility.formatMoney((slide2*100), window.globalVariables.money_format)}`);
  }
}
customElements.define('range-slider', PriceRangeSlider);